import { Component, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { logger } from '@/lib/logger';
import { captureException, isSentryEnabled } from '@/monitoring/sentry';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  routeName?: string;
  fallbackRoute?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Route-level Error Boundary
 * Catches errors within a specific route without affecting other routes
 * Provides route-specific recovery options
 */
class RouteErrorBoundaryClass extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      logger.error(
        `[RouteErrorBoundary:${this.props.routeName || 'Unknown'}] Caught error:`,
        error
      );
      logger.error('[RouteErrorBoundary] Error info:', errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Send to error tracking service
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService(error: Error, errorInfo: React.ErrorInfo) {
    // Send to Sentry via our monitoring module
    if (isSentryEnabled()) {
      captureException(error, {
        componentStack: errorInfo.componentStack,
        boundaryType: 'route',
        routeName: this.props.routeName || 'unknown',
      });
    }

    // Log to custom analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: false,
        route: this.props.routeName || 'unknown',
      });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { routeName = 'this page', fallbackRoute = '/dashboard' } = this.props;

      return (
        <RouteErrorFallback
          routeName={routeName}
          fallbackRoute={fallbackRoute}
          error={this.state.error}
          onReset={this.handleReset}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Fallback UI for route errors
 */
function RouteErrorFallback({
  routeName,
  fallbackRoute,
  error,
  onReset,
  onReload,
}: {
  routeName: string;
  fallbackRoute: string;
  error: Error | null;
  onReset: () => void;
  onReload: () => void;
}) {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate(fallbackRoute);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <CardTitle>Error loading {routeName}</CardTitle>
          </div>
          <CardDescription>
            Something went wrong while loading this page. You can try again or navigate to another
            page.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {import.meta.env.DEV && error && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Error Details (Dev Only):</p>
              <div className="bg-muted p-3 rounded-md overflow-auto max-h-40">
                <code className="text-xs text-muted-foreground">{error.message}</code>
              </div>
              {error.stack && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Stack trace
                  </summary>
                  <pre className="mt-2 bg-muted p-3 rounded-md overflow-auto max-h-60 text-muted-foreground">
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium">What you can do:</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Try again (reset the page state)</li>
              <li>Go to the dashboard</li>
              <li>Reload the entire app</li>
            </ul>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={onReset} className="w-full sm:w-auto">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
          <Button onClick={handleGoHome} variant="outline" className="w-full sm:w-auto">
            <Home className="h-4 w-4 mr-2" />
            Go to Dashboard
          </Button>
          <Button onClick={onReload} variant="ghost" className="w-full sm:w-auto">
            Reload App
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * Wrapper component to use RouteErrorBoundary with hooks
 */
export function RouteErrorBoundary({
  children,
  routeName,
  fallbackRoute,
}: RouteErrorBoundaryProps) {
  return (
    <RouteErrorBoundaryClass routeName={routeName} fallbackRoute={fallbackRoute}>
      {children}
    </RouteErrorBoundaryClass>
  );
}
